'use client';

import { Component, type ReactNode } from 'react';

type ErrorBoundaryProps = {
    children: ReactNode;
    /** Rendered when a child throws. Defaults to `null` (render nothing). */
    fallback?: ReactNode;
};

type ErrorBoundaryState = {
    hasError: boolean;
};

/**
 * A generic React error boundary.
 *
 * @remark
 * Suspense only handles the *pending* state of a promise. When a child
 * (including an async Server Component) throws/rejects, the error propagates to
 * the nearest error boundary. Without one, the whole route crashes. Wrap
 * non-critical UI in this boundary so a failure degrades gracefully instead of
 * taking down the page.
 */
export default class ErrorBoundary extends Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error) {
        console.error('ErrorBoundary caught error:', error.message);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback ?? null;
        }
        return this.props.children;
    }
}
